import type { ReaderTalkAnalysis } from '../../data/schema';

interface AnalysisRailOptions {
  talkId: string;
  analysis: ReaderTalkAnalysis | undefined;
  onOpen(title: string, body: HTMLElement, anchor: HTMLElement): void;
}

function evidenceLink(
  talkId: string,
  paragraphId: string,
  label: string,
): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = `/read/${talkId}/${paragraphId}`;
  link.dataset.router = '';
  link.className = 'analysis-evidence-link';
  link.textContent = label;
  return link;
}

function createAnalysisBody(
  talkId: string,
  analysis: ReaderTalkAnalysis,
): HTMLElement {
  const body = document.createElement('div');
  body.className = 'analysis-context';

  const disclosure = document.createElement('p');
  disclosure.className = 'source-callout source-callout--ai';
  disclosure.textContent =
    'This theme and sentiment analysis is an AI-generated study aid.';
  body.append(disclosure);

  const themeHeading = document.createElement('h3');
  themeHeading.textContent = 'Themes';
  body.append(themeHeading);
  for (const theme of analysis.themes) {
    const item = document.createElement('section');
    item.className = 'analysis-item';
    const heading = document.createElement('h4');
    heading.textContent = theme.name;
    const score = document.createElement('span');
    score.className = 'analysis-score';
    score.textContent = `${String(theme.prominence)} of 10`;
    heading.append(score);
    item.append(heading);
    if (theme.subThemes.length > 0) {
      const copy = document.createElement('p');
      copy.textContent = theme.subThemes.join(', ');
      item.append(copy);
    }
    if (theme.evidence.length > 0) {
      const links = document.createElement('div');
      links.className = 'analysis-evidence';
      for (const [index, evidence] of theme.evidence.slice(0, 3).entries()) {
        links.append(
          evidenceLink(
            talkId,
            evidence.paragraphId,
            `Evidence ${String(index + 1)}: ${evidence.text}`,
          ),
        );
      }
      item.append(links);
    }
    body.append(item);
  }

  const sentimentHeading = document.createElement('h3');
  sentimentHeading.textContent = 'Sentiment arc';
  body.append(sentimentHeading);
  const sentimentList = document.createElement('ol');
  sentimentList.className = 'sentiment-list';
  for (const segment of analysis.sentiment) {
    const item = document.createElement('li');
    const heading = document.createElement('strong');
    heading.textContent =
      `${segment.label} · intensity ${String(segment.intensity)} of 10`;
    const copy = document.createElement('p');
    copy.textContent = segment.description;
    item.append(heading, copy);
    const firstParagraphId = segment.paragraphIds[0];
    item.append(
      evidenceLink(talkId, firstParagraphId, 'Read the grounding passage'),
    );
    sentimentList.append(item);
  }
  body.append(sentimentList);
  return body;
}

export function createAnalysisRail(options: AnalysisRailOptions): HTMLElement {
  const rail = document.createElement('aside');
  rail.className = 'analysis-rail';
  rail.setAttribute('aria-label', 'Optional talk analysis');

  const track = document.createElement('div');
  track.className = 'analysis-rail__track';
  track.setAttribute('aria-hidden', 'true');
  const themes = options.analysis?.themes ?? [];
  for (const [index, theme] of themes.slice(0, 6).entries()) {
    const segment = document.createElement('span');
    segment.className = `analysis-rail__segment analysis-rail__segment--${String((index % 4) + 1)}`;
    segment.style.flexGrow = String(Math.max(theme.prominence, 1));
    track.append(segment);
  }

  const button = document.createElement('button');
  button.className = 'analysis-rail__button';
  button.type = 'button';
  button.textContent = 'Show analysis';
  button.disabled = !options.analysis;
  button.addEventListener('click', () => {
    if (!options.analysis) return;
    options.onOpen(
      'Themes and sentiment',
      createAnalysisBody(options.talkId, options.analysis),
      button,
    );
  });

  const copy = document.createElement('p');
  copy.textContent = options.analysis
    ? 'Theme and sentiment context stays hidden until you request it.'
    : 'Analysis is not available for this talk.';
  rail.append(track, button, copy);
  return rail;
}
